import { Box, ButtonProps, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import { GridAddIcon, GridDeleteIcon } from "@mui/x-data-grid";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { COLORS } from "../../assets/theme";
import Actions from "../../components/common/Actions";
import AutoComplete from "../../components/common/AutoComplete";
import CustomDateRangePicker from "../../components/common/CustomDateRangePicker";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import SearchForm from "../../components/common/SearchForm";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import ToastNotification from "../../components/ToastNotification";
import {
  addRoutingPlanApi,
  bulkUpdateCurrencyApi,
  deleteRoutingPlansApi,
  editRoutingPlanApi,
  exportRoutingPlan,
  fetchIRoutingPlanHistory,
  getRoutingPlanById,
  RoutingPlanType,
  useRoutingPlanQuery,
  useRoutingPlansQuery,
} from "../../services/RoutingPlanService";
import {
  DateRange,
  Option,
  IField,
  Params,
} from "../../types/table/interfaces.type";
import { useCustomerOptionsQuery } from "../../services/ClientService";
import {
  getCurrencyNameOptions,
  useCurrenciesQuery,
} from "../../services/CurrencyService";
import AccountNamesTable from "../../components/AccountnameDialog";

import History from "../../components/History";
import { hasJwtPermission } from "../../services/ServicesHelpers";

const CurrencyCell: React.FC<{ currencyId?: number | null }> = ({ currencyId }) => {
  const { data: currencyName } = useQuery({
    ...getCurrencyNameOptions(Number(currencyId)),
    enabled: !!currencyId,
  });

  if (!currencyId) {
    return (
      <Tooltip title="No currency assigned to this routing plan" placement="top" arrow>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <FlagIcon fontSize="small" sx={{ color: "error.main" }} />
          <Typography variant="body2" color="error.main">
            Missing
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  return <>{currencyName ?? "-"}</>;
};

const RoutingPlanPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [appliedFilters, setAppliedFilters] = useState<
    Params<RoutingPlanType & DateRange>
  >({
    search: "",
  });
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const dialogRef = useRef<RoutingPlanDialogRef>(null);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedRowIds([]);
  }, [fromDate, toDate]);

  const onRowSelect = (row: RoutingPlanType, checked: boolean) => {
    console.log(`Row ID ${row.id} selection changed: ${checked}`);
    setSelectedRowIds((prevSelected) => {
      const newSelected = checked
        ? [...prevSelected, String(row.id)]
        : prevSelected.filter((id) => id !== String(row.id));
      console.log("Selected row IDs:", newSelected);
      return newSelected;
    });
  };

  const isRowSelected = (row: RoutingPlanType) =>
    selectedRowIds.includes(String(row.id));

  const handleOpenDialog = (
    mode: "add" | "edit" | "delete" | "Accounts" | "history" | "updateCurrency",
    id?: string
  ) => {
    dialogRef.current?.openDialog(mode, id);
  };
  const [routingPlanOtions, setRoutingPlanOptions] = useState<Option[]>([]);

  const { data: routingPlansOptions = [] } = useRoutingPlansQuery();

  useEffect(() => {
    if (routingPlansOptions) {
      setRoutingPlanOptions(routingPlansOptions);
    }
  }, [routingPlansOptions]);
  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      fromDate: fromDate,
      toDate: toDate,
    }));
    setSelectedRowIds([]);
  };

  const actions = Actions({
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ROUTING_PLAN"),
    },
    // onDelete: {
    //   handler: async (id: string | number) => {
    //     console.log(`Delete action triggered for ID: ${id}`);
    //     handleOpenDialog("delete", String(id));
    //   },
    //   isDisabled: false,
    // },
    onListAccounts: {
      handler: (row: any) => {
        handleOpenDialog("Accounts", String(row.id));
      },
      isDisabled: !hasJwtPermission("GET_ROUTING_PLAN_ACCOUNTS"),
    },
    onViewHistory: {
      // isDisabled: true,
      handler: async (id: string | number) => {
        handleOpenDialog("history", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_ROUTING_PLANS_HISTORY"),
    },
  });

  const buttons = [
    {
      label: "Search",
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      permission: "GET_ALL_ROUTING_PLANS",
    },
  ];

  const fields: IField<RoutingPlanType>[] = [
    { key: "name", header: "Routing Plan Name" },
    {
      key: "currencyId",
      header: "Currency",
      transform: (row) => (
        <CurrencyCell currencyId={row.currencyId ?? null} />
      ),
    },
    { key: "createdDate", header: "Created Date" },
    { key: "createdBy", header: "User" },
  ];

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <CustomLabel
        text="Routing Plan"
        variant={isSmallScreen ? "h6" : "h5"}
        fontWeight={500}
        marginTop={1}
        marginBottom={2}
      />
      <Box sx={{ mb: 3 }}>
        <SearchForm
          dateComponent={
            <CustomDateRangePicker
              fromDate={fromDate}
              toDate={toDate}
              setFromDate={setFromDate}
              setToDate={setToDate}
            />
          }
          buttons={buttons}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="Routing Plan List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginTop={1}
          marginBottom={1}
        />
        <SearchHeaderContainer
          search={appliedFilters.search ?? ""}
          setSearch={handleSearchChange}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <GridAddIcon />,
              onClick: () => handleOpenDialog("add"),
              textColor: COLORS.iconColor,
              permission: "POST_ROUTING_PLAN",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              variant: "outlined" as ButtonProps["variant"],
              startIcon: <GridDeleteIcon />,
              onClick: () => handleOpenDialog("delete"),
              textColor: COLORS.iconColor,
              permission: "DELETE_ROUTING_PLAN",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              variant: "outlined" as ButtonProps["variant"],
              startIcon: <CurrencyExchangeIcon />,
              onClick: () => handleOpenDialog("updateCurrency"),
              textColor: COLORS.iconColor,
              permission: "PUT_ROUTING_PLAN_BULK_CURRENCY",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <Export />,

              onClick: async () => {
                try {
                  await exportRoutingPlan({
                    ...appliedFilters,
                    fromDate: fromDate ? fromDate.toISOString() : "",
                    toDate: toDate ? toDate.toISOString() : "",
                  });
                  ToastNotification.success("Export completed successfully!");
                } catch (error: any) {
                  ToastNotification.error(
                    error.message || "Export failed. Please try again."
                  );
                }
              },
              showExportProgress: true,
              textColor: COLORS.iconColor,
              label: "Export",
              permission: "GET_ALL_ROUTING_PLANS_CSV",
            },
          ]}
        />

        <CustomTable<RoutingPlanType>
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useRoutingPlanQuery}
          actions={actions}
          checkboxSelect={true}
          onRowSelect={onRowSelect}
          isRowSelected={isRowSelected}
          sortableFields={["name", "createdDate", "createdBy"]}
        />
      </Box>
      <RoutingPlanDialog
        ref={dialogRef}
        routingPlanOtions={routingPlanOtions}
        selectedRowIds={selectedRowIds}
        onClearSelection={() => setSelectedRowIds([])}
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries();
        }}
        appliedFilters={appliedFilters}
      />
    </Box>
  );
};

interface RoutingPlanDialogRef {
  openDialog: (
    mode: "add" | "edit" | "delete" | "Accounts" | "history" | "updateCurrency",
    id?: string
  ) => void;
}

interface RoutingPlanDialogProps {
  onRefresh: () => void;
  routingPlanOtions: Option[];
  selectedRowIds: string[];
  appliedFilters: Params<RoutingPlanType & DateRange>;
  onClearSelection: () => void;
}

const RoutingPlanDialog = forwardRef<
  RoutingPlanDialogRef,
  RoutingPlanDialogProps
>(
  (
    {
      onRefresh,
      routingPlanOtions,
      selectedRowIds,
      onClearSelection,
      appliedFilters,
    },
    ref
  ) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<
      "add" | "edit" | "delete" | "Accounts" | "history" | "updateCurrency"
    >("add");
    const [dialogData, setDialogData] = useState<Partial<RoutingPlanType>>({});
    const [selectedId, setSelectedId] = useState<string | undefined>();
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [bulkCurrencyId, setBulkCurrencyId] = useState<number | null>(null);

    const { data: fetchCurrencies = [] } = useCurrenciesQuery({});
    const currencyOptions = fetchCurrencies.map((c) => ({
      label: c.name,
      value: c.id,
    }));

    const { data: accountNames } = useCustomerOptionsQuery(
      dialogData.id ?? "",
      {
        enabled: Boolean(dialogData.id),
      }
    );
    useImperativeHandle(ref, () => ({
      openDialog: (mode, id) => {
        setDialogMode(mode);
        setSelectedId(id);
        setBulkCurrencyId(null);
        setDialogOpen(true);
        openDialog(mode, id);
      },
    }));

    const validateField = (field: string, value: string) => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: !value,
      }));
    };

    useEffect(() => {
      const isAnyFieldEmpty = !dialogData.name || !dialogData.currencyId;
      setIsSaveDisabled(isAnyFieldEmpty);
    }, [dialogData]);

    const resetDialogData = () => {
      setDialogData({});
      setDialogOpen(false);
      setIsSaveDisabled(true);
    };

    const openDialog = async (
      mode: "add" | "edit" | "delete" | "Accounts" | "history" | "updateCurrency",
      id?: string
    ) => {
      setDialogMode(mode);
      try {
        if (mode === "edit") {
          if (!id) {
            console.warn("ID is required to edit a routing plan.");
            return;
          }
          const routingPlanData = await getRoutingPlanById(id);
          setDialogData(routingPlanData);
        } else if (mode === "delete" && id) {
          setDialogData({ id });
        } else if (mode === "Accounts" && id) {
          setDialogData({ id });
        } else {
          setDialogData({
            name: "",
            createdDate: new Date().toISOString(),
            createdBy: "",
          });
        }
      } catch (error: any) {
        console.error(
          `Error fetching routing plan data for ${mode} mode:`,
          error
        );
      }
    };

    const addRoutingPlan = async (data: Partial<RoutingPlanType>) => {
      try {
        await addRoutingPlanApi(data);
        onRefresh();
        setDialogOpen(false);
        setDialogData({});
        ToastNotification.success("Routing plan added successfully!");
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const editRoutingPlan = async (data: Partial<RoutingPlanType>) => {
      try {
        if (!data.id) {
          console.warn("ID is missing for editing the routing plan.");
          ToastNotification.warning("Routing plan ID is missing.");
          return;
        }

        const { id, ...dataToUpdate } = data;
        const updatedPlan = await editRoutingPlanApi(id, {
          ...dataToUpdate,
          createdDate: data.createdDate,
        });

        console.log("Routing plan updated successfully:", updatedPlan);
        onRefresh();
        setDialogOpen(false);
        ToastNotification.success("Routing plan updated successfully!");
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const deleteRoutingPlan = async () => {
      try {
        await deleteRoutingPlansApi(selectedRowIds);
        ToastNotification.success(
          "Selected routing plans deleted successfully!"
        );
        onClearSelection();
        onRefresh();
        setDialogOpen(false);
      } catch (error: any) {
        ToastNotification.error(
          error?.message || "Error deleting selected routing plans."
        );
      }
    };

    const bulkUpdateCurrency = async () => {
      if (!bulkCurrencyId) return;
      try {
        const updated = await bulkUpdateCurrencyApi(selectedRowIds, bulkCurrencyId);
        ToastNotification.success(
          `${updated.length} routing plan${updated.length !== 1 ? "s" : ""} updated successfully!`
        );
        onClearSelection();
        onRefresh();
        setDialogOpen(false);
        setBulkCurrencyId(null);
      } catch (error: any) {
        ToastNotification.error(
          error?.message || "Error updating currencies for selected routing plans."
        );
      }
    };

    const fieldHistory: IField<RoutingPlanType & { historyTimestamp: Date }>[] =
      [
        { key: "name", header: "Routing Plan Name" },
        {
          key: "currencyId",
          header: "Currency",
          transform: (row) => (
            <CurrencyCell currencyId={row.currencyId ?? null} />
          ),
        },
        { key: "createdBy", header: "User" },
        {
          key: "historyTimestamp",
          header: "Date Modified",
        },
        { key: "createdBy", header: "Created By" },
        { key: "updatedBy", header: "Updated By" },
      ];
    return (
      <CustomDialog
        open={dialogOpen}
        onClose={resetDialogData}
        mode={dialogMode}
        title={
          dialogMode === "history"
            ? "History"
            : dialogMode === "add"
            ? "Add Routing Plan"
            : dialogMode === "edit"
            ? "Edit Routing Plan"
            : dialogMode === "Accounts"
            ? "Linked Accounts Names"
            : dialogMode === "updateCurrency"
            ? "Update Currency"
            : "Delete Routing Plan"
        }
        fields={
          dialogMode === "history"
            ? [
                {
                  type: "custom",
                  component: (
                    <History
                      filters={appliedFilters}
                      fields={fieldHistory}
                      fetchFunc={async ({ size = 10 }) => {
                        const result = await fetchIRoutingPlanHistory(
                          String(selectedId),
                          {
                            size,
                          }
                        );
                        return {
                          ...result,
                          content: result.content.map((item: any) => ({
                            ...item,
                            historyTimestamp:
                              item.historyTimestamp ?? new Date(),
                          })),
                        };
                      }}
                    />
                  ),
                },
              ]
            : dialogMode === "delete"
            ? [
                {
                  type: "custom",
                  component: (
                    <Box>
                      <p>Are you sure you want to delete this ?</p>
                    </Box>
                  ),
                },
              ]
            : dialogMode === "updateCurrency"
            ? [
                {
                  type: "custom",
                  component: (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Select a currency to apply to{" "}
                        <strong>{selectedRowIds.length}</strong> selected routing
                        plan{selectedRowIds.length !== 1 ? "s" : ""}.
                      </Typography>
                      <AutoComplete
                        label="Currency"
                        value={bulkCurrencyId ?? ""}
                        onChange={(value: string | number | (string | number)[]) => {
                          setBulkCurrencyId(Number(value));
                        }}
                        highlightLabelStar={true}
                        options={currencyOptions}
                        placeholder="Select Currency"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    </Box>
                  ),
                },
              ]
            : dialogMode === "Accounts"
            ? [
                {
                  type: "custom",
                  component: (
                    <AccountNamesTable accountNames={accountNames || []} />
                  ),
                },
              ]
            : [
                {
                  type: "text",
                  label: "Name Route Plan",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  value: dialogData.name || "",
                  onChange: (value) => {
                    setDialogData((prev) => ({
                      ...prev,
                      name: String(value),
                    }));
                    validateField("name", value as string);
                  },
                  placeholder: "Enter Name",
                  error: !!errors.name,
                  helperText: errors.name ? "Name is required" : "",
                },
                {
                  type: "custom",
                  component: (
                    <AutoComplete
                      label="Currency"
                      value={dialogData.currencyId ?? ""}
                      onChange={(value: string | number | (string | number)[]) => {
                        setDialogData((prev) => ({
                          ...prev,
                          currencyId: Number(value),
                        }));
                        validateField("currencyId", String(value));
                      }}
                      highlightLabelStar={true}
                      options={currencyOptions}
                      placeholder="Select Currency"
                      error={!!errors.currencyId}
                      helperText={errors.currencyId ? "Currency is required" : ""}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: COLORS.WhiteBackground,
                        },
                      }}
                    />
                  ),
                },
                {
                  type: "custom",
                  component: (
                    <AutoComplete
                      label="Clone of"
                      value={dialogData.cloneOf || ""}
                      onChange={(value: string | number | (string | number)[]) => {
                        setDialogData((prev) => ({
                          ...prev,
                          cloneOf: String(value),
                        }));
                      }}
                      clearable={true}
                      options={routingPlanOtions}
                      placeholder="Select Clone of"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: COLORS.WhiteBackground,
                        },
                      }}
                    />
                  ),
                },
              ]
        }
        buttons={
          dialogMode === "history"
            ? []
            : dialogMode === "Accounts"
            ? []
            : dialogMode === "delete"
            ? [
                {
                  label: "Delete",
                  variant: "contained" as "contained",
                  bgColor: COLORS.GOLD,
                  textColor: COLORS.iconColor,
                  borderRadius: "12px",
                  onClick: deleteRoutingPlan,
                  disabled: !hasJwtPermission("DELETE_ROUTING_PLAN"),
                },
              ]
            : dialogMode === "updateCurrency"
            ? [
                {
                  label: "Update",
                  variant: "contained" as "contained",
                  bgColor: COLORS.GOLD,
                  textColor: COLORS.iconColor,
                  borderRadius: "12px",
                  onClick: bulkUpdateCurrency,
                  disabled: !bulkCurrencyId || !hasJwtPermission("PUT_ROUTING_PLAN_BULK_CURRENCY"),
                },
              ]
            : [
                {
                  label: "Save",
                  variant: "contained" as "contained",
                  bgColor: COLORS.GOLD,
                  borderRadius: "12px",
                  onClick: async () => {
                    if (dialogMode === "edit") {
                      await editRoutingPlan(dialogData);
                    } else {
                      await addRoutingPlan(dialogData);
                    }
                  },
                  disabled: isSaveDisabled || (dialogMode === "edit" ? !hasJwtPermission("PUT_ROUTING_PLAN") : !hasJwtPermission("POST_ROUTING_PLAN")),
                },
              ]
        }
      />
    );
  }
);
export default RoutingPlanPage;
